import type { PropsWithChildren } from 'react';
import React from 'react';

import { Box, keyframes } from '@mui/material';

import { Z_INDEX } from '@/constants/z-index';

type ElectricBorderProps = PropsWithChildren<{
  color?: string;
  pulsate?: boolean;
  durationMs?: number;
  animate?: boolean;
}>;

const ElectricBorder: React.FC<ElectricBorderProps> = ({
  children,
  color = '#E84E0F',
  pulsate = true,
  durationMs = 1500,
  animate = true
}: ElectricBorderProps) => {
  const baseW = 2;
  const blur1 = 1 + baseW * 0.5;
  const blur2 = 2 + baseW;
  const minW = 2;
  const maxW = 6;
  const pulseWidth = keyframes({
    '0%': { borderWidth: `${minW}px` },
    '50%': { borderWidth: `${maxW}px` },
    '100%': { borderWidth: `${minW}px` }
  });
  const glow1MinBlur = 1 + minW * 0.5;
  const glow1MaxBlur = 1 + maxW * 0.5;
  const glow2MinBlur = 2 + minW;
  const glow2MaxBlur = 2 + maxW;
  const pulseGlow1 = keyframes({
    '0%': { filter: `blur(${glow1MinBlur}px)` },
    '50%': { filter: `blur(${glow1MaxBlur}px)` },
    '100%': { filter: `blur(${glow1MinBlur}px)` }
  });
  const pulseGlow2 = keyframes({
    '0%': { filter: `blur(${glow2MinBlur}px)` },
    '50%': { filter: `blur(${glow2MaxBlur}px)` },
    '100%': { filter: `blur(${glow2MinBlur}px)` }
  });

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 'inherit',
        overflow: 'visible'
      }}
    >
      {animate && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            zIndex: Z_INDEX.sticky
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              boxSizing: 'border-box',
              borderStyle: 'solid',
              borderColor: `${color}`,
              borderWidth: `${baseW}px`,
              ...(pulsate && {
                animation: `${pulseWidth} ${durationMs}ms ease-in-out infinite`
              })
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              boxSizing: 'border-box',
              borderStyle: 'solid',
              borderColor: `oklch(from ${color} l c h / 0.6)`,
              borderWidth: `${baseW}px`,
              opacity: 0.7,
              filter: `blur(${blur1}px)`,
              ...(pulsate && {
                animation: `${pulseWidth} ${durationMs}ms ease-in-out infinite, ${pulseGlow1} ${durationMs}ms ease-in-out infinite`
              })
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              boxSizing: 'border-box',
              borderStyle: 'solid',
              borderColor: `oklch(from ${color} l c h)`,
              borderWidth: `${baseW}px`,
              opacity: 0.6,
              filter: `blur(${blur2}px)`,
              ...(pulsate && {
                animation: `${pulseWidth} ${durationMs}ms ease-in-out infinite, ${pulseGlow2} ${durationMs}ms ease-in-out infinite`
              })
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          position: 'relative',
          borderRadius: 'inherit',
          height: '100%'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ElectricBorder;
